(() => {
  if (window.lnvcCollectionFilters) return;
  const selector = '.lnvc-shop-results';
  const section = () => document.querySelector(selector);
  // Established outside the replaceable results section before any AJAX response.
  const resultsAnnouncer = document.querySelector('[data-lnvc-results-announcer]');
  let controller;
  let revision = 0;
  let timer;
  let pendingUrl = new URL(window.location.href);
  let renderedUrl = new URL(window.location.href);
  let headerFrame;
  const headerTransitions = new Set();
  const headerSection = () => document.querySelector('.section-header') || document.querySelector('.shopify-section-header');
  const syncToolbarOffset = () => {
    const header = headerSection();
    const toolbar = section()?.querySelector('[data-lnvc-shopping-toolbar]');
    const rect = header?.getBoundingClientRect();
    const sticky = header && getComputedStyle(header).position === 'sticky';
    const offset = sticky ? Math.max(0, rect.bottom) : 0;
    document.documentElement.style.setProperty('--lnvc-sticky-header-offset', `${offset}px`);
    document.documentElement.style.setProperty('--lnvc-shopping-toolbar-height', `${toolbar?.getBoundingClientRect().height || 78}px`);
  };
  const runToolbarFrame = () => {
    syncToolbarOffset();
    if (headerTransitions.size) headerFrame = requestAnimationFrame(runToolbarFrame);
  };
  const scheduleToolbarOffset = () => {
    cancelAnimationFrame(headerFrame);
    headerFrame = requestAnimationFrame(runToolbarFrame);
  };
  const focusProductList = (root = section()) => {
    syncToolbarOffset();
    const grid = root?.querySelector('#ProductGridContainer');
    grid?.focus({ preventScroll: true });
    grid?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  const syncTasteCards = () => {
    document.querySelectorAll('.lnvc-taste-navigator [data-lnvc-taste-role]').forEach((card) => {
      const control = section()?.querySelector(`[data-lnvc-taste-role="${card.dataset.lnvcTasteRole}"]`);
      if (!control) return;
      card.href = control.dataset.lnvcTasteUrl || control.href;
      const active = control.classList.contains('is-active');
      card.classList.toggle('is-active', active);
      if (active) card.setAttribute('aria-current', 'true');
      else card.removeAttribute('aria-current');
      card.toggleAttribute('data-lnvc-remove-filter', control.hasAttribute('data-lnvc-remove-filter'));
    });
  };
  // Dawn enhances summaries once at page load; section responses need the same
  // accessible state without rebinding the custom element's drawer handlers.
  const initializeFacetSummaries = (root) => {
    root.querySelector('.mobile-facets__disclosure > summary')?.setAttribute('tabindex', '-1');
    root.querySelectorAll('[id^="Details-"] > summary').forEach((summary) => {
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', summary.parentElement.open ? 'true' : 'false');
      const panelId = summary.nextElementSibling?.id;
      if (panelId) summary.setAttribute('aria-controls', panelId);
      summary.addEventListener('click', () => {
        summary.setAttribute('aria-expanded', summary.parentElement.open ? 'false' : 'true');
      });
    });
  };
  const closeFilterDrawer = () => {
    const root = section();
    const drawer = root?.querySelector('.mobile-facets__disclosure');
    if (!drawer) return;
    drawer.removeAttribute('open');
    drawer.classList.remove('menu-opening');
    drawer.querySelectorAll('details[open]').forEach((item) => { item.removeAttribute('open'); item.classList.remove('menu-opening'); });
    drawer.querySelectorAll('.submenu-open').forEach((item) => item.classList.remove('submenu-open'));
    drawer.querySelectorAll('summary[aria-expanded]').forEach((item) => item.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop');
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    const trigger = root.querySelector('[data-lnvc-open-filters]');
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.focus();
  };
  const invalidate = () => {
    clearTimeout(timer);
    controller?.abort();
    return ++revision;
  };
  const updateResults = async (url, { pushHistory = true, focusResults = false } = {}) => {
    const requestRevision = invalidate();
    pendingUrl = new URL(url);
    controller = new AbortController();
    const current = section();
    if (!current) return;
    current.setAttribute('aria-busy', 'true');
    try {
      const requestUrl = new URL(url);
      requestUrl.searchParams.set('section_id', current.dataset.sectionId);
      const response = await fetch(requestUrl.href, { signal: controller.signal });
      if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
      const html = await response.text();
      if (requestRevision !== revision) return;
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      if (!next) throw new Error('Collection results were not found');
      // DOMParser parses noscript markup with scripting disabled. Its fallback
      // styles would become active when moved into this JavaScript-enabled page.
      next.querySelectorAll('noscript').forEach((fallback) => fallback.remove());
      const openIds = [...current.querySelectorAll('details[open]')].map((item) => item.id);
      const drawerOpen = current.querySelector('.mobile-facets__disclosure')?.open;
      const focusId = document.activeElement?.id;
      const filterTriggerFocused = document.activeElement?.matches('[data-lnvc-open-filters]');
      const removedChipFocused = document.activeElement?.matches('.lnvc-filter-drawer__applied a');
      const drawerScroll = current.querySelector('.mobile-facets__main')?.scrollTop || 0;
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      current.replaceWith(next);
      const resultCount = next.querySelector('.lnvc-shop-toolbar__count')?.textContent.trim();
      if (resultsAnnouncer && resultCount && resultsAnnouncer.textContent !== resultCount) resultsAnnouncer.textContent = resultCount;
      next.querySelectorAll('[data-lnvc-filter-accordion]').forEach((details) => {
        details.open = openIds.includes(details.id);
      });
      initializeFacetSummaries(next);
      renderedUrl = new URL(url);
      syncTasteCards();
      syncToolbarOffset();
      if (next.dataset.documentTitle) document.title = next.dataset.documentTitle;
      if (pushHistory && url.href !== window.location.href) history.pushState({ lnvcCollection: true }, '', url.href);
      if (drawerOpen) {
        const drawer = next.querySelector('.mobile-facets__disclosure');
        drawer?.setAttribute('open', '');
        drawer?.classList.add('menu-opening');
        openIds.forEach((id) => {
          const details = id && document.getElementById(id);
          if (!details) return;
          details.setAttribute('open', '');
          if (!details.hasAttribute('data-lnvc-filter-accordion')) details.classList.add('menu-opening');
          details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
          if (!details.hasAttribute('data-lnvc-filter-accordion')) details.closest('.has-submenu')?.classList.add('submenu-open');
        });
        const drawerMain = next.querySelector('.mobile-facets__main');
        if (drawerMain) drawerMain.scrollTop = drawerScroll;
        next.querySelector('[data-lnvc-open-filters]')?.setAttribute('aria-expanded', 'true');
        const focused = focusId && document.getElementById(focusId);
        (focused || (removedChipFocused && next.querySelector('.lnvc-filter-drawer__applied a, [data-lnvc-filter-accordion] > summary')) || next.querySelector('.lnvc-filter-drawer__close'))?.focus({ preventScroll: true });
      } else {
        const restored = (focusId && document.getElementById(focusId)) || (filterTriggerFocused && next.querySelector('[data-lnvc-open-filters]'));
        const grid = next.querySelector('#ProductGridContainer');
        (focusResults ? grid : restored || grid)?.focus({ preventScroll: true });
        if (focusResults) focusProductList(next);
      }
      next.querySelectorAll('.scroll-trigger').forEach((item) => item.classList.add('scroll-trigger--cancel'));
    } catch (error) {
      if (requestRevision !== revision || error.name === 'AbortError') return;
      window.location.assign(url.href);
    } finally {
      if (requestRevision === revision) section()?.removeAttribute('aria-busy');
    }
  };
  const remove = (href, clear = false, options = {}) => {
    const clearHref = clear && section()?.querySelector('[data-lnvc-all-coffee]')?.href;
    const url = new URL(clearHref || href, window.location.href);
    // Shopify's removal links describe the rendered results, which may lag behind inputs.
    const removed = [...renderedUrl.searchParams].filter(([name, value]) =>
      name.startsWith('filter.') && !url.searchParams.getAll(name).includes(value));
    [...url.searchParams.keys()].filter((name) => name.startsWith('filter.')).forEach((name) => url.searchParams.delete(name));
    if (!clear) {
      const removesPrice = removed.some(([name]) => name === 'filter.v.price.gte' || name === 'filter.v.price.lte');
      for (const [name, value] of pendingUrl.searchParams) {
        if (!name.startsWith('filter.')) continue;
        if (removesPrice && (name === 'filter.v.price.gte' || name === 'filter.v.price.lte')) continue;
        if (removed.some(([removedName, removedValue]) => name === removedName && value === removedValue)) continue;
        url.searchParams.append(name, value);
      }
    }
    url.searchParams.set('view', 'lnvc');
    if (pendingUrl.searchParams.has('sort_by')) url.searchParams.set('sort_by', pendingUrl.searchParams.get('sort_by'));
    url.searchParams.delete('page');
    return updateResults(url, options);
  };
  window.lnvcCollectionFilters = { updateResults, remove };
  const onInput = (event) => {
    const input = event.target;
    if (!input.closest(`${selector} facet-filters-form`) || !input.name) return;
    const url = new URL(pendingUrl);
    url.searchParams.delete('page');
    url.searchParams.set('view', 'lnvc');
    url.searchParams.delete(input.name);
    const form = input.closest('form');
    for (const control of form.elements) {
      if (control.name !== input.name || control.disabled) continue;
      if ((control.type === 'checkbox' || control.type === 'radio') && !control.checked) continue;
      if (control.value !== '') url.searchParams.append(control.name, control.value);
    }
    pendingUrl = url;
    invalidate();
    timer = setTimeout(() => updateResults(url), input.tagName === 'SELECT' || input.type === 'checkbox' ? 0 : 450);
  };
  document.addEventListener('input', onInput);
  document.addEventListener('change', onInput);
  document.addEventListener('submit', (event) => {
    if (event.target.closest(`${selector} facet-filters-form`)) { event.preventDefault(); updateResults(pendingUrl); }
  });
  document.addEventListener('click', (event) => {
    const accordionSummary = event.target.closest('[data-lnvc-filter-accordion] > summary');
    if (accordionSummary?.closest(selector)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const details = accordionSummary.parentElement;
      details.open = !details.open;
      accordionSummary.setAttribute('aria-expanded', String(details.open));
      return;
    }
    const trigger = event.target.closest('[data-lnvc-open-filters]');
    if (trigger) {
      event.preventDefault();
      const drawer = section()?.querySelector('.mobile-facets__disclosure');
      drawer?.querySelector(':scope > summary')?.click();
      trigger.setAttribute('aria-expanded', drawer?.open ? 'true' : 'false');
      requestAnimationFrame(() => section()?.querySelector('.lnvc-filter-drawer__close')?.focus());
      return;
    }
    if (event.target.closest('[data-lnvc-close-filters]')) {
      event.preventDefault(); event.stopPropagation(); closeFilterDrawer(); return;
    }
    const done = event.target.closest('.mobile-facets__footer button');
    if (done && done.closest(selector)) {
      event.preventDefault(); event.stopPropagation(); closeFilterDrawer(); return;
    }
    const anchor = event.target.closest('a[href]');
    if (anchor && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && !anchor.target) {
      const destination = new URL(anchor.href, window.location.href);
      if (destination.hash === '#ProductGridContainer' && destination.pathname === window.location.pathname && destination.search === window.location.search && !anchor.hasAttribute('data-lnvc-collection-filter')) {
        event.preventDefault();
        history.replaceState(history.state, '', destination.href);
        focusProductList();
        return;
      }
    }
    const link = event.target.closest('[data-lnvc-collection-filter], [data-lnvc-remove-filter], [data-lnvc-clear-filters], .lnvc-shop-results .pagination a');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    event.preventDefault();
    if (link.hasAttribute('data-lnvc-remove-filter') || link.hasAttribute('data-lnvc-clear-filters')) {
      remove(url.href, link.hasAttribute('data-lnvc-clear-filters'), { focusResults: Boolean(link.closest('.lnvc-taste-navigator')) }); return;
    }
    if (link.hasAttribute('data-lnvc-collection-filter')) {
      const allCoffee = link.hasAttribute('data-lnvc-all-coffee');
      if (allCoffee) url.searchParams.delete('filter.p.m.custom.tasting_notes');
      const destinationNames = new Set(url.searchParams.keys());
      for (const [name, value] of pendingUrl.searchParams) {
        if (allCoffee && name === 'filter.p.m.custom.tasting_notes') continue;
        if ((name.startsWith('filter.') || name === 'sort_by') && !destinationNames.has(name)) url.searchParams.append(name, value);
      }
      url.searchParams.set('view', 'lnvc');
      url.searchParams.delete('page');
    }
    updateResults(url, { focusResults: Boolean(link.closest('.pagination, .lnvc-taste-navigator, .lnvc-shop-promoted')) });
  }, true);
  document.addEventListener('keydown', (event) => {
    const openDrawer = section()?.querySelector('.mobile-facets__disclosure[open]');
    if (!openDrawer) return;
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeFilterDrawer(); return; }
    if (event.key !== 'Tab') return;
    const visibleControls = [...openDrawer.querySelectorAll('.mobile-facets__inner button:not([disabled]), .mobile-facets__inner a[href], .mobile-facets__inner input:not([disabled]):not([type="hidden"]), .mobile-facets__inner select:not([disabled]), .mobile-facets__inner summary')].filter((control) => control.getClientRects().length && !control.closest('details:not([open]) .mobile-facets__submenu, details:not([open]) .lnvc-filter-accordion__panel'));
    const first = visibleControls[0]; const last = visibleControls.at(-1);
    if (event.shiftKey && (document.activeElement === first || !openDrawer.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || !openDrawer.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
  }, true);
  document.addEventListener('keyup', (event) => {
    if (event.key === 'Escape' && event.target.closest(selector)) event.stopPropagation();
  }, true);
  if (section()) initializeFacetSummaries(section());
  syncTasteCards();
  syncToolbarOffset();
  window.addEventListener('scroll', scheduleToolbarOffset, { passive: true });
  window.addEventListener('resize', scheduleToolbarOffset);
  const stickyHeader = headerSection();
  if (stickyHeader) {
    const trackHeaderTransition = (event) => {
      if (event.target !== stickyHeader || !['top', 'transform', 'translate'].includes(event.propertyName)) return;
      if (event.type === 'transitionrun') headerTransitions.add(event.propertyName);
      else headerTransitions.delete(event.propertyName);
      scheduleToolbarOffset();
    };
    stickyHeader.addEventListener('transitionrun', trackHeaderTransition);
    stickyHeader.addEventListener('transitionend', trackHeaderTransition);
    stickyHeader.addEventListener('transitioncancel', trackHeaderTransition);
    new MutationObserver(scheduleToolbarOffset).observe(stickyHeader, { attributes: true, attributeFilter: ['class'] });
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(scheduleToolbarOffset).observe(stickyHeader);
  }
  window.addEventListener('load', () => { if (window.location.hash === '#ProductGridContainer') focusProductList(); });
  window.addEventListener('popstate', () => { if (section()) updateResults(new URL(window.location.href), { pushHistory: false }); });
})();
